import { cfgSupportChatApiUrl } from '@infinitro/config';
import { Verbiage, t } from '@infinitro/content';
import { locale, parseUtcTimestamp } from '@infinitro/global';
import { derived, get, writable } from 'svelte/store';
import { connectSupportChatSocket, createSupportAttachmentUpload, createSupportConversation, getSupportConversation, getSupportConversationMessages, getSupportChatActor, isAllowedSupportChatAttachmentType, isCustomerBannedError, listSupportConversations, refreshGuestSupportChatSession, requestSupportHandoff as requestSupportHandoffRest, sendSupportMessage, supportChatMessageCharacterLength, truncateToMessageCharacterLength, SUPPORT_CHAT_ATTACHMENT_MAX_BYTES, SUPPORT_CHAT_GUEST_ATTACHMENT_MAX_BYTES, SUPPORT_CHAT_MESSAGE_MAX_CHARACTERS, SupportChatApiError, SupportChatMessageTooLongError, uploadSupportAttachmentToS3 } from './api';
import { supportChatDebug, supportChatWarn } from './debug';
import { resolveSupportChatAttachmentUrl } from './utils/support-chat-attachment-url';
const MESSAGES_PAGE_SIZE = 50;
const MAX_RECONNECT_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 6;
const SERVER_SHUTDOWN_COOLDOWN_MS = 15_000;
/** True once the initial REST fetch has completed at least once. */
export const isSupportChatReady = writable(false);
export const stateSupportChatConversations = writable([]);
/** Messages keyed by conversation id. Deduped by message.id, sorted by created_at. */
export const stateSupportChatMessagesById = writable({});
export const isSupportChatWsConnected = writable(false);
/** True after a `server_shutdown` event, until the next successful reconnect. */
export const isSupportChatServerShutdown = writable(false);
export const stateSupportChatLastError = writable(null);
/**
 * True once a create-conversation or send-message call has been refused with 403 `customer_banned`.
 * Cleared as soon as either write path succeeds again (the ban lifts server-side on un-spam).
 */
export const isSupportChatCustomerBanned = writable(false);
/** Per-conversation: messages already fetched (used as next offset). */
export const stateLoadingMoreMessages = writable({});
/** Per-conversation: whether more pages exist beyond the loaded ones. */
export const stateConversationHasMore = writable({});
/**
 * Conversation id for which the UI should show an "agent is typing" placeholder until the latest
 * message becomes an agent/system reply (REST send or WS).
 */
export const pendingAgentReplyConversationId = writable(null);
/** Show AI-unavailable banner when send/WS reports `ai_status` unavailable|failed. */
export const stateSupportChatAiUnavailableById = writable({});
/** Show human-joined banner after `assigned` WS until dismissed by navigation or close. */
export const stateSupportChatAssignedNoticeById = writable({});
/**
 * Fallback anchoring for the synthetic "Support joined" notice. When the backend emits a bare
 * `assigned` WS event (no timestamped system message), this holds the id of the last known message
 * at assignment time so the notice can be placed before the first agent reply that follows it.
 */
export const stateSupportChatAssignedAfterMessageIdById = writable({});
// --- Module-local orchestration state -----------------------------------------
let disconnectSocket = null;
let reconnectTimer = null;
let reconnectAttempt = 0;
let intentionalDisconnect = false;
let serverShutdownUntil = 0;
/** Bumped by every start()/stop() so a start() suspended at the actor-resolution await can detect
 *  it was superseded and abort instead of silently discarding a newer socket on resume. */
let startGen = 0;
/** Tracks which conversations have had their initial page fetched. */
const historyLoaded = new Set();
/** Number of messages already fetched per conversation (next page offset). */
const messageFetchedCount = new Map();
/** Total messages reported by the API per conversation. */
const messageTotalCount = new Map();
// --- Helpers ------------------------------------------------------------------
function isSupportChatConfigured() {
    return Boolean(cfgSupportChatApiUrl()?.trim());
}
function browserLocaleForSupportChat() {
    const loc = get(locale)?.trim();
    return loc || undefined;
}
function isCustomerSender(senderType) {
    const st = (senderType || '').toLowerCase();
    return st === 'customer' || st === 'user' || st === 'player';
}
/** Backend may flag join/system events via `message_type` or `sender_type`. */
export function isSystemSupportMessage(m) {
    const senderType = (m.sender_type ?? '').toLowerCase();
    return m.message_type === 'system' || senderType === 'system';
}
function isAiAgentSender(senderType) {
    const st = (senderType ?? '').toLowerCase();
    return st === 'ai_agent' || st === 'ai_bot';
}
export function isTerminalSupportConversationStatus(status) {
    return status === 'resolved' || status === 'closed' || status === 'spam';
}
export function isAiHandlingConversation(c) {
    if (!c)
        return false;
    if (c.status === 'queued_for_human' || c.status === 'human_handling')
        return false;
    return c.status === 'ai_handling';
}
function firstActiveConversation(list) {
    return list.find((c) => !isTerminalSupportConversationStatus(c.status));
}
export function supportMessageToRow(m) {
    const displayBody = m.is_translated && m.translated_content != null && m.translated_content !== ''
        ? m.translated_content
        : m.content;
    const role = isSystemSupportMessage(m)
        ? 'system'
        : isCustomerSender(m.sender_type)
            ? 'user'
            : 'agent';
    return {
        role,
        body: displayBody,
        createdAt: m.created_at,
        senderType: m.sender_type,
        senderName: m.sender_name?.trim() || undefined,
        attachments: (m.attachments ?? []).map((a) => ({
            url: resolveSupportChatAttachmentUrl(a.url),
            contentType: a.content_type,
            filename: a.filename ?? undefined
        }))
    };
}
function setAiUnavailable(conversationId, show) {
    stateSupportChatAiUnavailableById.update((map) => {
        if (show)
            return { ...map, [conversationId]: true };
        if (!(conversationId in map))
            return map;
        const next = { ...map };
        delete next[conversationId];
        return next;
    });
}
function setAssignedNotice(conversationId, show) {
    stateSupportChatAssignedNoticeById.update((map) => {
        if (show)
            return { ...map, [conversationId]: true };
        if (!(conversationId in map))
            return map;
        const next = { ...map };
        delete next[conversationId];
        return next;
    });
}
function setAssignedBoundary(conversationId, afterMessageId) {
    stateSupportChatAssignedAfterMessageIdById.update((map) => ({
        ...map,
        [conversationId]: afterMessageId
    }));
}
function clearAssignedBoundary(conversationId) {
    stateSupportChatAssignedAfterMessageIdById.update((map) => {
        if (!(conversationId in map))
            return map;
        const next = { ...map };
        delete next[conversationId];
        return next;
    });
}
function applyAiStatusOutcome(conversationId, aiStatus, conv) {
    if (!aiStatus || aiStatus === 'responded' || aiStatus === 'not_applicable') {
        setAiUnavailable(conversationId, false);
        return;
    }
    if (aiStatus === 'queued_for_human') {
        setAiUnavailable(conversationId, false);
        pendingAgentReplyConversationId.update((cur) => (cur === conversationId ? null : cur));
        return;
    }
    if (aiStatus === 'unavailable' || aiStatus === 'failed') {
        setAiUnavailable(conversationId, true);
        pendingAgentReplyConversationId.update((cur) => (cur === conversationId ? null : cur));
        return;
    }
    if (isAiHandlingConversation(conv)) {
        pendingAgentReplyConversationId.set(conversationId);
    }
}
function supportMessageContent(message) {
    if (!message)
        return '';
    return message.is_translated &&
        message.translated_content != null &&
        message.translated_content !== ''
        ? message.translated_content
        : message.content;
}
function conversationListPreview(c) {
    return c.last_message || c.last_message_content || '';
}
function conversationListUpdatedAt(c) {
    return c.last_message_at ?? c.updated_at ?? c.created_at;
}
function previewFromMessages(messages, fallback = '') {
    if (!messages.length)
        return fallback;
    return supportMessageContent(messages[messages.length - 1]);
}
function shortUpdatedLabel(iso) {
    if (!iso)
        return '';
    try {
        const ms = parseUtcTimestamp(iso);
        if (Number.isNaN(ms))
            return '';
        const diff = Date.now() - ms;
        if (diff < 60_000)
            return 'Just now';
        const d = new Date(ms);
        if (diff < 86_400_000)
            return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    catch {
        return '';
    }
}
function sortedUniqueMerge(prev, incoming) {
    const byId = new Map();
    for (const m of prev)
        byId.set(m.id, m);
    // Incoming wins so WS-updated fields (e.g. confidence_score) override REST — except
    // sender_name: a partial update that omits it must not blank out a previously known
    // historical-author snapshot (see supportMessageToRow).
    for (const m of incoming) {
        const known = byId.get(m.id);
        byId.set(m.id, m.sender_name ? m : { ...m, sender_name: known?.sender_name });
    }
    return Array.from(byId.values()).sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
}
function storeMessages(conversationId, incoming) {
    if (!incoming.length)
        return;
    supportChatDebug('Store messages', {
        conversationId,
        incomingCount: incoming.length
    });
    stateSupportChatMessagesById.update((map) => ({
        ...map,
        [conversationId]: sortedUniqueMerge(map[conversationId] ?? [], incoming)
    }));
    const merged = get(stateSupportChatMessagesById)[conversationId] ?? [];
    const last = merged[merged.length - 1];
    if (last && !isCustomerSender(last.sender_type)) {
        pendingAgentReplyConversationId.update((cur) => (cur === conversationId ? null : cur));
    }
}
function removeMessage(conversationId, messageId) {
    supportChatDebug('Remove message', { conversationId, messageId });
    stateSupportChatMessagesById.update((map) => {
        const current = map[conversationId] ?? [];
        const nextMessages = current.filter((m) => m.id !== messageId);
        if (nextMessages.length === current.length)
            return map;
        return { ...map, [conversationId]: nextMessages };
    });
}
function upsertConversation(c) {
    supportChatDebug('Upsert conversation', {
        conversationId: c.id,
        status: c.status,
        agentName: c.current_agent?.name ?? null
    });
    stateSupportChatConversations.update((list) => {
        const idx = list.findIndex((x) => x.id === c.id);
        if (idx < 0)
            return [c, ...list];
        const next = list.slice();
        next[idx] = c;
        return next;
    });
}
// --- Derived views ------------------------------------------------------------
/** True once an inline system notice (real backend system message, or synthetic) exists. */
function hasSupportJoinedSystemNotice(rows) {
    return rows.some((r) => r.role === 'system');
}
/**
 * Fallback only. The backend emitted an `assigned` event but no timestamped system message, so we
 * synthesize a single "Support joined" row. It is anchored immediately before the first agent
 * message that arrived after assignment (`afterMessageId`, matched against `messages` since rows are
 * index-aligned). If no such agent message exists yet, the notice is appended so it sits next to the
 * pending agent reply. Never added when a real system message is already present (dedup), and the
 * deterministic recompute means re-renders/reconnects cannot produce duplicates.
 */
function withSyntheticAssignedNotice(rows, messages, showAssigned, afterMessageId) {
    if (!showAssigned || hasSupportJoinedSystemNotice(rows))
        return rows;
    const notice = { role: 'system', body: '', synthetic: true };
    const anchorIndex = rows.findIndex((row, i) => row.role === 'agent' &&
        !isAiAgentSender(row.senderType) &&
        (afterMessageId == null || messages[i].id > afterMessageId));
    if (anchorIndex < 0)
        return [...rows, notice];
    return [...rows.slice(0, anchorIndex), notice, ...rows.slice(anchorIndex)];
}
export const stateSupportChatThreads = derived([
    stateSupportChatConversations,
    stateSupportChatMessagesById,
    stateSupportChatAssignedNoticeById,
    stateSupportChatAssignedAfterMessageIdById
], ([conversations, byId, assignedById, assignedAfterById]) => {
    const supportTitle = get(t)(Verbiage.support);
    return conversations.map((c) => {
        const msgs = byId[c.id] ?? [];
        const last = msgs[msgs.length - 1];
        const agentName = c.current_agent?.name?.trim() || undefined;
        const rows = withSyntheticAssignedNotice(msgs.map(supportMessageToRow), msgs, assignedById[c.id] ?? false, assignedAfterById[c.id] ?? null);
        return {
            id: c.id,
            title: agentName || supportTitle,
            agentName,
            preview: previewFromMessages(msgs, conversationListPreview(c)),
            updatedLabel: shortUpdatedLabel(last?.created_at ?? conversationListUpdatedAt(c)),
            messages: rows,
            status: c.status,
            isAiHandled: c.is_ai_handled,
            isHumanHandled: c.is_human_handled
        };
    });
});
export const stateSupportChatConversationStatusById = derived(stateSupportChatConversations, (conversations) => {
    const out = {};
    for (const c of conversations)
        out[c.id] = c.status;
    return out;
});
// --- REST orchestration -------------------------------------------------------
function hydrateMissingConversationPreviews(list) {
    for (const conversation of list) {
        const existingMessages = get(stateSupportChatMessagesById)[conversation.id] ?? [];
        if (existingMessages.length || conversationListPreview(conversation))
            continue;
        void loadMessagesForConversation(conversation.id);
    }
}
export async function refreshSupportConversations() {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Refresh conversations skipped: support chat API is not configured');
        return;
    }
    stateSupportChatLastError.set(null);
    try {
        supportChatDebug('Refresh conversations start');
        const list = await listSupportConversations();
        stateSupportChatConversations.set(list);
        isSupportChatReady.set(true);
        hydrateMissingConversationPreviews(list);
        supportChatDebug('Refresh conversations complete', { count: list.length });
    }
    catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        stateSupportChatLastError.set(msg);
        supportChatWarn('List conversations failed', { error: e, message: msg });
    }
}
export async function refreshSupportConversation(conversationId) {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Refresh conversation skipped: support chat API is not configured', {
            conversationId
        });
        return;
    }
    try {
        supportChatDebug('Refresh conversation start', { conversationId });
        const c = await getSupportConversation(conversationId);
        upsertConversation(c);
        supportChatDebug('Refresh conversation complete', {
            conversationId,
            status: c.status
        });
    }
    catch (e) {
        supportChatWarn('Refresh conversation failed', { conversationId, error: e });
    }
}
export async function loadMessagesForConversation(conversationId, opts = {}) {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Load messages skipped: support chat API is not configured', {
            conversationId
        });
        return;
    }
    if (!opts.force && historyLoaded.has(conversationId)) {
        supportChatDebug('Load messages skipped: history already loaded', { conversationId });
        return;
    }
    try {
        supportChatDebug('Load messages start', {
            conversationId,
            force: Boolean(opts.force)
        });
        const page = await getSupportConversationMessages(conversationId, MESSAGES_PAGE_SIZE, 0);
        storeMessages(conversationId, page.messages);
        historyLoaded.add(conversationId);
        messageFetchedCount.set(conversationId, page.messages.length);
        messageTotalCount.set(conversationId, page.total);
        stateConversationHasMore.update((s) => ({
            ...s,
            [conversationId]: page.messages.length < page.total
        }));
        supportChatDebug('Load messages complete', {
            conversationId,
            messageCount: page.messages.length,
            total: page.total
        });
    }
    catch (e) {
        supportChatWarn('Load messages failed', { conversationId, error: e });
    }
}
export async function loadMoreMessagesForConversation(conversationId) {
    if (!isSupportChatConfigured())
        return;
    const fetched = messageFetchedCount.get(conversationId) ?? 0;
    const total = messageTotalCount.get(conversationId) ?? 0;
    if (fetched >= total)
        return;
    stateLoadingMoreMessages.update((s) => ({ ...s, [conversationId]: true }));
    try {
        supportChatDebug('Load more messages start', { conversationId, offset: fetched, total });
        const page = await getSupportConversationMessages(conversationId, MESSAGES_PAGE_SIZE, fetched);
        storeMessages(conversationId, page.messages);
        const newFetched = fetched + page.messages.length;
        messageFetchedCount.set(conversationId, newFetched);
        messageTotalCount.set(conversationId, page.total);
        stateConversationHasMore.update((s) => ({
            ...s,
            [conversationId]: newFetched < page.total
        }));
        supportChatDebug('Load more messages complete', {
            conversationId,
            newFetched,
            total: page.total
        });
    }
    catch (e) {
        supportChatWarn('Load more messages failed', { conversationId, error: e });
    }
    finally {
        stateLoadingMoreMessages.update((s) => ({ ...s, [conversationId]: false }));
    }
}
/** Opens a conversation view: refreshes status and loads its first page of messages. */
export async function openSupportConversation(conversationId) {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Open conversation skipped: support chat API is not configured', {
            conversationId
        });
        return;
    }
    supportChatDebug('Open conversation', { conversationId });
    await Promise.all([
        loadMessagesForConversation(conversationId),
        refreshSupportConversation(conversationId)
    ]);
}
/**
 * Re-sync after a gap in realtime (missed heartbeat, evicted channel, or any reconnect). The
 * socket does not replay what it missed, and a plain conversation-list refresh does not backfill
 * an already-loaded thread (see hydrateMissingConversationPreviews + the historyLoaded
 * short-circuit in loadMessagesForConversation). Messages are oldest-first, so the gap is at the
 * end: page forward from the fetched counter rather than reloading from offset 0. storeMessages
 * merges by id, so overlap with what's already displayed is harmless.
 */
async function resyncAfterRealtimeGap() {
    await refreshSupportConversations();
    for (const conversationId of [...historyLoaded]) {
        let offset = messageFetchedCount.get(conversationId) ?? 0;
        for (;;) {
            let page;
            try {
                page = await getSupportConversationMessages(conversationId, MESSAGES_PAGE_SIZE, offset);
            }
            catch (e) {
                supportChatWarn('Realtime gap re-sync failed', { conversationId, offset, error: e });
                break;
            }
            storeMessages(conversationId, page.messages);
            offset += page.messages.length;
            messageFetchedCount.set(conversationId, offset);
            messageTotalCount.set(conversationId, page.total);
            stateConversationHasMore.update((s) => ({ ...s, [conversationId]: offset < page.total }));
            // Mandatory, not defensive: `total` counts internal ops notes the player-facing find
            // endpoint hides, so on threads with internal notes offset can never reach total.
            if (page.messages.length === 0 || offset >= page.total)
                break;
        }
    }
}
// --- WebSocket realtime -------------------------------------------------------
function clearReconnectTimer() {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
}
function scheduleReconnect() {
    if (intentionalDisconnect) {
        supportChatDebug('Reconnect skipped: disconnect was intentional');
        return;
    }
    if (!isSupportChatConfigured()) {
        supportChatWarn('Reconnect skipped: missing config', {
            configured: isSupportChatConfigured()
        });
        return;
    }
    if (reconnectTimer) {
        supportChatDebug('Reconnect skipped: timer already scheduled');
        return;
    }
    // Cap the exponential growth at MAX_RECONNECT_ATTEMPTS instead of giving up there — realtime
    // must survive a slow deploy or a backgrounded tab rather than going silently dead.
    const backoff = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** Math.min(reconnectAttempt, MAX_RECONNECT_ATTEMPTS));
    reconnectAttempt = Math.min(reconnectAttempt + 1, MAX_RECONNECT_ATTEMPTS);
    const cooldown = Math.max(0, serverShutdownUntil - Date.now());
    // Jitter unsynchronizes a herd of clients that all lost the connection at the same moment
    // (e.g. a server restart) so they don't all reconnect on the same tick.
    const jitter = Math.floor(Math.random() * Math.min(1000, backoff));
    const delay = Math.max(backoff, cooldown) + jitter;
    supportChatDebug('Reconnect scheduled', {
        reconnectAttempt,
        delay,
        cooldown,
        jitter
    });
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void startSupportChatRealtime();
    }, delay);
}
export async function startSupportChatRealtime() {
    const gen = ++startGen;
    if (!isSupportChatConfigured()) {
        supportChatWarn('Realtime start skipped: missing config', {
            configured: isSupportChatConfigured()
        });
        return;
    }
    const actor = await getSupportChatActor();
    if (gen !== startGen)
        return;
    if (!actor) {
        supportChatWarn('Realtime start skipped: missing customer identity');
        return;
    }
    intentionalDisconnect = false;
    disconnectSocket?.();
    isSupportChatWsConnected.set(false);
    clearReconnectTimer();
    supportChatDebug('Realtime start', { actorKind: actor.kind });
    disconnectSocket = connectSupportChatSocket(actor, {
        onConnectionChange: (open, close) => {
            isSupportChatWsConnected.set(open);
            supportChatDebug('Realtime connection changed', { open, close });
            if (open) {
                // Read before the reset: >0 means this open followed a real reconnect (bootstrap
                // and deliberate replaces never bump this), so messages may have been missed.
                const wasReconnect = reconnectAttempt > 0;
                reconnectAttempt = 0;
                clearReconnectTimer();
                isSupportChatServerShutdown.set(false);
                if (wasReconnect)
                    void resyncAfterRealtimeGap();
                return;
            }
            // 1001 = the server is going away in some form (shutdown, evicted a slow socket, or
            // caught us mid-drain). Back off harder so we don't reconnect straight into a
            // still-restarting server.
            if (close?.code === 1001) {
                serverShutdownUntil = Date.now() + SERVER_SHUTDOWN_COOLDOWN_MS;
                // `channel closed` is also a healthy server evicting a socket that fell behind —
                // the backend itself can't tell those apart, so don't claim "restarting" for it.
                if (close.reason !== 'channel closed')
                    isSupportChatServerShutdown.set(true);
            }
            // Heartbeat timeout or an evicted channel: we were provably absent or behind, and the
            // socket never replays what it missed.
            if (close?.code === 4000 || close?.reason === 'channel closed') {
                void resyncAfterRealtimeGap();
            }
            scheduleReconnect();
        },
        onMessage: ({ conversation_id, message }) => {
            supportChatDebug('Realtime message event', {
                conversationId: conversation_id,
                messageId: message.id
            });
            storeMessages(conversation_id, [message]);
        },
        onMessageDeleted: ({ conversation_id, message_id }) => {
            supportChatDebug('Realtime message_deleted event', {
                conversationId: conversation_id,
                messageId: message_id
            });
            removeMessage(conversation_id, message_id);
        },
        onHandoff: ({ conversation_id }) => {
            supportChatDebug('Realtime handoff event', { conversationId: conversation_id });
            setAiUnavailable(conversation_id, false);
            pendingAgentReplyConversationId.update((cur) => (cur === conversation_id ? null : cur));
            void refreshSupportConversation(conversation_id);
            void loadMessagesForConversation(conversation_id, { force: true });
        },
        onAssigned: ({ conversation_id }) => {
            supportChatDebug('Realtime assigned event', { conversationId: conversation_id });
            // Capture the last known message id so the synthetic notice (fallback, when the backend
            // sends no timestamped system message) can anchor before the first reply that follows.
            const known = get(stateSupportChatMessagesById)[conversation_id] ?? [];
            setAssignedBoundary(conversation_id, known.length ? known[known.length - 1].id : null);
            setAssignedNotice(conversation_id, true);
            setAiUnavailable(conversation_id, false);
            pendingAgentReplyConversationId.update((cur) => (cur === conversation_id ? null : cur));
            void refreshSupportConversation(conversation_id);
        },
        onAiStatus: ({ conversation_id, status }) => {
            supportChatDebug('Realtime ai_status event', { conversationId: conversation_id, status });
            const conv = get(stateSupportChatConversations).find((c) => c.id === conversation_id);
            applyAiStatusOutcome(conversation_id, status, conv);
            if (status === 'queued_for_human')
                void refreshSupportConversation(conversation_id);
        },
        onConversationClosed: ({ conversation_id }) => {
            supportChatDebug('Realtime conversation_closed event', { conversationId: conversation_id });
            setAiUnavailable(conversation_id, false);
            setAssignedNotice(conversation_id, false);
            clearAssignedBoundary(conversation_id);
            pendingAgentReplyConversationId.update((cur) => (cur === conversation_id ? null : cur));
            void refreshSupportConversation(conversation_id);
        },
        onConversationReopened: ({ conversation_id }) => {
            supportChatDebug('Realtime conversation_reopened event', { conversationId: conversation_id });
            // The ops "restore from spam" action is the un-ban path — clear the client-side
            // ban flag so the banner/composer update live instead of waiting for the next
            // failed-then-retried write.
            isSupportChatCustomerBanned.set(false);
            void refreshSupportConversation(conversation_id);
        },
        onServerShutdown: () => {
            serverShutdownUntil = Date.now() + SERVER_SHUTDOWN_COOLDOWN_MS;
            isSupportChatServerShutdown.set(true);
            supportChatWarn('Realtime server shutdown event', {
                serverShutdownUntil
            });
        }
    });
}
export function stopSupportChatRealtime() {
    supportChatDebug('Realtime stop');
    startGen += 1;
    intentionalDisconnect = true;
    clearReconnectTimer();
    reconnectAttempt = 0;
    disconnectSocket?.();
    disconnectSocket = null;
    isSupportChatWsConnected.set(false);
}
/** Fully resets client state — useful on logout or widget teardown. */
export function resetSupportChatSession() {
    supportChatDebug('Session reset');
    stopSupportChatRealtime();
    historyLoaded.clear();
    messageFetchedCount.clear();
    messageTotalCount.clear();
    stateSupportChatConversations.set([]);
    stateSupportChatMessagesById.set({});
    isSupportChatReady.set(false);
    isSupportChatServerShutdown.set(false);
    stateSupportChatLastError.set(null);
    isSupportChatCustomerBanned.set(false);
    stateLoadingMoreMessages.set({});
    stateConversationHasMore.set({});
    pendingAgentReplyConversationId.set(null);
    stateSupportChatAiUnavailableById.set({});
    stateSupportChatAssignedNoticeById.set({});
    stateSupportChatAssignedAfterMessageIdById.set({});
}
// --- Session bootstrap --------------------------------------------------------
async function bootstrapSupportChatSessionImpl() {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Bootstrap skipped: support chat API is not configured', {
            apiUrl: cfgSupportChatApiUrl() ?? ''
        });
        return;
    }
    const actor = await getSupportChatActor();
    if (!actor) {
        supportChatWarn('Bootstrap skipped: missing customer identity');
        resetSupportChatSession();
        return;
    }
    supportChatDebug('Bootstrap session', {
        apiUrl: cfgSupportChatApiUrl(),
        actorKind: actor.kind
    });
    // Always reset realtime before (re)fetching so logout / missing token cannot leave a stale socket.
    stopSupportChatRealtime();
    if (actor.kind === 'guest')
        await refreshGuestSupportChatSession(actor.deviceId);
    await refreshSupportConversations();
    await startSupportChatRealtime();
}
/**
 * Mount and the loggedIn subscription can each call this. Chained (not coalesced) so the second
 * bootstrap's identity swap always runs after the first's, instead of interleaving with it.
 */
let bootstrapChain = Promise.resolve();
export function bootstrapSupportChatSession() {
    bootstrapChain = bootstrapChain
        .then(() => bootstrapSupportChatSessionImpl())
        .catch((e) => supportChatWarn('Bootstrap failed', { error: e }));
    return bootstrapChain;
}
/**
 * Returns the ID of the first existing conversation for the current customer, or creates
 * a new one if none exist. Does NOT send any message.
 */
export async function ensureDefaultSupportConversation() {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Ensure default conversation skipped: support chat API is not configured');
        return undefined;
    }
    if (!(await getSupportChatActor())) {
        supportChatWarn('Ensure default conversation skipped: missing customer identity');
        return undefined;
    }
    const list = get(stateSupportChatConversations);
    const existing = firstActiveConversation(list)?.id;
    if (existing) {
        supportChatDebug('Ensure default conversation: using existing', { conversationId: existing });
        return existing;
    }
    try {
        supportChatDebug('Ensure default conversation: creating new');
        const created = await createSupportConversation({
            browserLocale: browserLocaleForSupportChat()
        });
        isSupportChatCustomerBanned.set(false);
        upsertConversation(created);
        historyLoaded.add(created.id);
        supportChatDebug('Ensure default conversation: created', { conversationId: created.id });
        return created.id;
    }
    catch (e) {
        if (isCustomerBannedError(e))
            isSupportChatCustomerBanned.set(true);
        const msg = e instanceof Error ? e.message : String(e);
        stateSupportChatLastError.set(msg);
        supportChatWarn('Ensure default conversation failed', { error: e, message: msg });
        return undefined;
    }
}
/** GIF is appended as its own trailing line in `content`, matching the BO frontend's format. */
export function planSupportMessageSendSteps(text, gif, imageFile) {
    const trimmedText = text.trim();
    const trimmedGif = gif.trim();
    if (!trimmedText && !trimmedGif && !imageFile)
        return [];
    const body = trimmedText && trimmedGif ? `${trimmedText}\n${trimmedGif}` : trimmedText || trimmedGif;
    return [{ body, imageFile }];
}
export { supportChatMessageCharacterLength, truncateToMessageCharacterLength, SUPPORT_CHAT_MESSAGE_MAX_CHARACTERS };
/**
 * Ensures a conversation exists for the current customer, sends the message, and stores returned
 * messages (customer plus optional synchronous AI reply). Additional replies may arrive via the
 * `message` WS event.
 */
export async function sendUserMessage(content, opts = {}) {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Send message skipped: support chat API is not configured', {
            contentLength: content.length
        });
        return undefined;
    }
    const actor = await getSupportChatActor();
    if (!actor) {
        stateSupportChatLastError.set('Support chat identity is not available');
        supportChatWarn('Send message skipped: missing customer identity');
        return undefined;
    }
    stateSupportChatLastError.set(null);
    const list = get(stateSupportChatConversations);
    let conversationId = firstActiveConversation(list)?.id;
    const imageFile = opts.imageFile ?? null;
    const trimmedContent = content.trim();
    try {
        supportChatDebug('Send message start', {
            contentLength: trimmedContent.length,
            existingConversationId: conversationId ?? null,
            hasImage: Boolean(imageFile)
        });
        if (supportChatMessageCharacterLength(trimmedContent) > SUPPORT_CHAT_MESSAGE_MAX_CHARACTERS) {
            throw new SupportChatMessageTooLongError(SUPPORT_CHAT_MESSAGE_MAX_CHARACTERS);
        }
        if (!conversationId) {
            const created = await createSupportConversation({
                browserLocale: browserLocaleForSupportChat()
            });
            conversationId = created.id;
            upsertConversation(created);
            historyLoaded.add(conversationId);
        }
        let attachmentRefs;
        if (imageFile) {
            const contentType = imageFile.type;
            if (!isAllowedSupportChatAttachmentType(contentType)) {
                throw new SupportChatApiError('Attachment type not allowed', 400);
            }
            const maxAttachmentBytes = actor.kind === 'guest'
                ? SUPPORT_CHAT_GUEST_ATTACHMENT_MAX_BYTES
                : SUPPORT_CHAT_ATTACHMENT_MAX_BYTES;
            if (imageFile.size > maxAttachmentBytes) {
                throw new SupportChatApiError('Attachment too large', 413);
            }
            const presign = await createSupportAttachmentUpload(conversationId, contentType, imageFile.size);
            await uploadSupportAttachmentToS3(presign, imageFile, contentType);
            attachmentRefs = [{ attachment_id: presign.attachment_id, filename: imageFile.name }];
        }
        const result = await sendSupportMessage(conversationId, {
            content: trimmedContent,
            attachments: attachmentRefs,
            browserLocale: browserLocaleForSupportChat()
        });
        isSupportChatCustomerBanned.set(false);
        storeMessages(conversationId, result.messages);
        const conv = get(stateSupportChatConversations).find((c) => c.id === conversationId);
        applyAiStatusOutcome(conversationId, result.ai_status, conv);
        const msgsAfter = get(stateSupportChatMessagesById)[conversationId] ?? [];
        const lastAfter = msgsAfter[msgsAfter.length - 1];
        if (lastAfter &&
            isCustomerSender(lastAfter.sender_type) &&
            isAiHandlingConversation(conv) &&
            result.ai_status !== 'unavailable' &&
            result.ai_status !== 'failed') {
            pendingAgentReplyConversationId.set(conversationId);
        }
        supportChatDebug('Send message complete', {
            conversationId,
            messageIds: result.messages.map((m) => m.id),
            aiStatus: result.ai_status ?? null
        });
        return conversationId;
    }
    catch (e) {
        if (isCustomerBannedError(e))
            isSupportChatCustomerBanned.set(true);
        const msg = e instanceof SupportChatMessageTooLongError
            ? get(t)(Verbiage.support_chat_message_too_long_error)
            : e instanceof SupportChatApiError && e.status === 400 && imageFile
                ? get(t)(Verbiage.support_chat_attachment_error)
                : e instanceof Error
                    ? e.message
                    : String(e);
        stateSupportChatLastError.set(msg);
        supportChatWarn('Send message failed', { error: e, message: msg, conversationId });
        if (conversationId) {
            pendingAgentReplyConversationId.update((cur) => (cur === conversationId ? null : cur));
        }
        return undefined;
    }
}
export async function requestSupportHandoff(conversationId, reason) {
    if (!isSupportChatConfigured()) {
        supportChatWarn('Request handoff skipped: support chat API is not configured', {
            conversationId
        });
        return;
    }
    try {
        supportChatDebug('Request handoff start', { conversationId, reason });
        await requestSupportHandoffRest(conversationId, reason);
        // Status will also refresh on the incoming `handoff` WS event; refetch defensively
        // so the UI reflects `queued_for_human` immediately on success.
        void refreshSupportConversation(conversationId);
        supportChatDebug('Request handoff complete', { conversationId });
    }
    catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        stateSupportChatLastError.set(msg);
        supportChatWarn('Request handoff failed', {
            conversationId,
            reason,
            error: e,
            message: msg
        });
        throw e;
    }
}
